import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import "./bookingSettings.css";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";

const API_URL = REST_API_URL;

export default function BookingSettingsPage() {
  const { t } = useTranslation();
  const [choices, setChoices] = useState(null);
  const [selectedTone, setSelectedTone] = useState(null);
  const [responseTime, setResponseTime] = useState(null);
  const [duration, setDuration] = useState(null);
  const [customDuration, setCustomDuration] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(false);

  const navigate = useNavigate();

  const isButtonDisabled =
    !selectedTone ||
    !responseTime ||
    !duration ||
    (duration === "Other" && !customDuration);

  useEffect(() => {
    const userStatus = localStorage.getItem("user_status") || "individual";
    const bookingCode = userStatus === "salon" ? "232" : "220";
    const currentCode = localStorage.getItem("onboarding_code");

    if (currentCode !== "complete") {
      localStorage.setItem("onboarding_code", bookingCode);
    }

    sessionStorage.setItem("onboarding_force_route", "/booking-settings");
    sessionStorage.setItem("onboarding_force_status", userStatus);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem("booking_settings_closed", "true");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    const wasClosed = localStorage.getItem("booking_settings_closed");
    if (wasClosed === "true") {
      localStorage.removeItem("booking_settings_closed");
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const getValidToken = async () => {
      let savedToken =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (!savedToken) return null;

      const verifyHeaders = buildHeaders();

      const verify = await fetch(`${API_URL}/auth/token/verify/`, {
        method: "POST",
        headers: verifyHeaders,
        body: JSON.stringify({ token: savedToken }),
      });

      if (verify.ok) {
        setToken(savedToken);
        return savedToken;
      }

      const refresh =
        localStorage.getItem("refreshToken") ||
        sessionStorage.getItem("refreshToken");

      if (!refresh) return null;

      const res = await fetch(`${API_URL}/auth/token/refresh/`, {
        method: "POST",
        headers: verifyHeaders,
        body: JSON.stringify({ refresh }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data.access) {
        const useLocalStorage =
          !!localStorage.getItem("accessToken") ||
          !!localStorage.getItem("refreshToken");

        if (useLocalStorage) {
          localStorage.setItem("accessToken", data.access);
        } else {
          sessionStorage.setItem("accessToken", data.access);
        }

        setToken(data.access);
        return data.access;
      }

      return null;
    };

    getValidToken();
  }, []);

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([fetchChoices()]);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchChoices = async () => {
    try {
      const headers = buildHeaders();

      const res = await fetch(`${API_URL}/service/booking-choices/`, {
        method: "GET",
        headers,
      });

      if (!res.ok) throw new Error(String(res.status));

      const data = await res.json().catch(() => null);
      setChoices(data);
    } catch (err) {
      console.error("Failed to load booking choices:", err);
    }
  };

  

  const saveBookingSettings = async () => {
    if (!selectedTone) {
      toast.error(t("auth.bookingPageErrorSelectTone"));
      return;
    }

    if (!responseTime) {
      toast.error(t("auth.bookingPageErrorSelectResponseTime"));
      return;
    }

    if (!duration) {
      toast.error(t("auth.bookingPageErrorSelectDuration"));
      return;
    }

    let finalDuration;

    if (duration === "0") {
      finalDuration = 0;
    } else if (duration === "Other") {
      finalDuration = Number(customDuration);
    } else {
      finalDuration = Number(duration);
    }

    if (Number.isNaN(finalDuration)) {
      toast.error(t("auth.bookingPageErrorInvalidDuration"));
      return;
    }

    const userTimeZone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

    const payload = {
      duration: finalDuration,
      time_unit: "minutes",
      response_time: responseTime,
      tone_of_voice_id: Number(selectedTone),
      timeZone: userTimeZone,
    };

    try {
      setIsLoading(true);

      const headers = buildHeaders();
      const res = await fetch(`${API_URL}/service/booking-time/`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error("Failed to save booking settings");
      }

      localStorage.removeItem("booking_settings_closed");

      const bookingSettings = {
        duration: finalDuration,
        responseTime,
        selectedTone,
        userTimeZone,
      };

      const userStatus = localStorage.getItem("user_status") || "individual";
      const nextCode = userStatus === "salon" ? "238" : "240";

      localStorage.setItem("booking_settings", JSON.stringify(bookingSettings));
      localStorage.setItem("onboarding_code", nextCode);

      sessionStorage.setItem("onboarding_force_route", "/how-did-you-find");
      sessionStorage.setItem("onboarding_force_status", userStatus);

      toast.success(t("auth.bookingPageSuccessOnboarding"));
      navigate("/how-did-you-find", { replace: true });
    } catch (err) {
      console.error("Save error:", err);
      toast.error(t("auth.bookingPageErrorSaveSettings"));
    } finally {
      setIsLoading(false);
    }
  };

  const durationOptions = [
    { label: t("auth.bookingPageDurationNone"), value: "0" },
    { label: t("auth.bookingPageDuration15min"), value: "15" },
    { label: t("auth.bookingPageDuration30min"), value: "30" },
    { label: t("auth.bookingPageDuration1hour"), value: "60" },
    { label: t("auth.bookingPageDuration2hours"), value: "120" },
    { label: t("auth.bookingPageDurationOther"), value: "Other" },
  ];

  const isCustom = duration && !["0", "15", "30", "60", "120", "Other"].includes(duration);

  const formatDuration = (value) => {
    if (!value) return t("auth.bookingPageSelectDuration");
    if (value === "0") return t("auth.bookingPageDurationNone");

    const minutes = parseInt(value, 10);
    if (!minutes) return t("auth.bookingPageDurationNone");

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours && !mins)
      return t("auth.bookingPageFormatHoursOnly", {
        hours,
        s: hours > 1 ? "s" : "",
      });
    if (hours && mins)
      return t("auth.bookingPageFormatHoursMin", {
        hours,
        s: hours > 1 ? "s" : "",
        mins,
      });

    return t("auth.bookingPageFormatMinsOnly", { mins });
  };

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (e.key === "Enter" && !isButtonDisabled && !isLoading) {
        e.preventDefault();
        saveBookingSettings();
      }
    };

    window.addEventListener("keydown", handleEnterKey);
    return () => window.removeEventListener("keydown", handleEnterKey);
  }, [
    isButtonDisabled,
    isLoading,
    selectedTone,
    responseTime,
    duration,
    customDuration,
  ]);

  if (!token) {
    return <div className="no-token"></div>;
  }

  if (isLoading || !choices) {
    return <div className="loading">{t("auth.loading")}</div>;
  }

  return (
    <div className="booking-wrapper">
      <motion.div
        className="booking-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="header">
          <div className="titles">
            <h1>{t("auth.bookingPageTitle")}</h1>
            <p>{t("auth.bookingPageSubtitle")}</p>
          </div>
        </div>

        <div className="section-row">
          <div className="left">
            <h2>{t("auth.bookingPageBufferTimeDesc")}</h2>
            <label className="label">
              {t("auth.bookingPageChooseDuration")}
            </label>

            <div className="booking-select-wrapper">
              <div
                className="booking-select-display"
                onClick={() => setOpenDropdown(!openDropdown)}
              >
                <span>
                  {!duration
                    ? t("auth.bookingPageSelectDuration")
                    : duration === "0"
                      ? t("auth.bookingPageDurationNone")
                      : duration === "Other"
                        ? customDuration
                          ? formatDuration(customDuration)
                          : t("auth.bookingPageDurationCustom")
                        : formatDuration(duration)}
                </span>

                <span className={`booking-arrow ${openDropdown ? "open" : ""}`}>
                  ▾
                </span>
              </div>

              <AnimatePresence>
                {openDropdown && (
                  <motion.div
                    className="booking-dropdown"
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2 }}
                  >
                    {durationOptions.map((opt) => (
                      <div
                        key={opt.value}
                        className={`booking-dropdown-option ${
                          duration === opt.value || (opt.value === "Other" && isCustom) ? "active" : ""
                        }`}
                        onClick={() => {
                          setDuration(opt.value);
                          if (opt.value !== "Other") {
                            setCustomDuration("");
                            setOpenDropdown(false);
                          }
                        }}
                      >
                        <span>{opt.label}</span>
                        <div
                          className={`booking-option-check ${
                            duration === opt.value || (opt.value === "Other" && isCustom) ? "checked" : ""
                          }`}
                        ></div>
                      </div>
                    ))}

                    {(duration === "Other" || isCustom) && (
                      <motion.div
                        className="dropdown-custom-input"
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="number"
                          min="1"
                          placeholder={t("auth.bookingPageDurationPlaceholder")}
                          value={customDuration}
                          onChange={(e) => setCustomDuration(e.target.value)}
                          className="dropdown-custom-field"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && customDuration) {
                              e.preventDefault();
                              const minutes = parseInt(customDuration, 10);
                              if (!Number.isNaN(minutes) && minutes > 0) {
                                setDuration(minutes.toString());
                                setCustomDuration(minutes.toString());
                                setOpenDropdown(false);
                              }
                            }
                          }}
                        />
                        <button
                          className="dropdown-apply-btn"
                          onClick={() => {
                            const minutes = parseInt(customDuration, 10);
                            if (Number.isNaN(minutes) || minutes <= 0) {
                              toast.error(
                                t("auth.bookingPageErrorInvalidDuration"),
                              );
                              return;
                            }

                            setDuration(minutes.toString());
                            setCustomDuration(minutes.toString());
                            setOpenDropdown(false);
                          }}
                        >
                          {t("auth.bookingPageApply")}
                        </button>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="right">
            <h2>{t("auth.bookingPageResponseTimeTitle")}</h2>
            <div className="radio-list">
              {choices.response_time_choices?.map((item, i) => (
                <label key={i} className="radio-option">
                  <span>{t(`settings.${item.value}`)}</span>
                  <input
                    type="radio"
                    value={item.value}
                    checked={responseTime === item.value}
                    onChange={() => setResponseTime(item.value)}
                  />
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="tone-section">
          <h2>{t("auth.bookingPageToneTitle")}</h2>
          <div className="tone-grid">
            {choices.tone_of_voice_options?.map((tone) => (
              <motion.label
                key={tone.id}
                className={`tone-card ${selectedTone === tone.id ? "active" : ""}`}
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedTone(tone.id)}
              >
                <div className="tone-content">
                  <div>
                    <h3>{tone.title}</h3>
                    <p>{tone.description}</p>
                  </div>
                  <span className="tone-check"></span>
                </div>
              </motion.label>
            ))}
          </div>
        </div>

        <div className="footer">
          <button
            onClick={saveBookingSettings}
            className="btn-primary"
            disabled={isButtonDisabled || isLoading}
            style={{
              opacity: isButtonDisabled ? 0.5 : 1,
              cursor: isButtonDisabled ? "not-allowed" : "pointer",
            }}
          >
            {isLoading ? t("auth.saving") : t("auth.bookingPageFinishBtn")}
          </button>
          <p className="step">{t("auth.bookingPageStep")}</p>
        </div>
      </motion.div>
    </div>
  );
}
